import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../../context/StoreContext';
import { Link, useNavigate } from 'react-router-dom';

export default function Header() {
  const { 
    cart, 
    wishlist, 
    setCartOpen, 
    setFilters, 
    filters,
    isLoggedIn,
    userEmail,
    setLoginModalOpen,
    logout
  } = useStore();
  
  const navigate = useNavigate();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef(null);
  
  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);
  const wishlistCount = wishlist.length;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavigation = (path) => {
    setUserMenuOpen(false);
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/90 glass border-b border-slate-200">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6">
        <div className="flex items-center gap-4 lg:gap-8 h-[68px]">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-indigo-600 grid place-items-center shadow-lg shadow-fuchsia-500/25">
              <i className="ri-flashlight-line text-white text-xl"></i>
            </div>
            <div>
              <div className="font-display text-[22px] leading-none font-semibold tracking-tight">TRENDOVA</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 -mt-0.5">Style Redefined</div>
            </div>
          </Link>

          <div className="flex-1 hidden md:flex items-center justify-center">
            <div className="w-full max-w-[520px] relative group">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]"></i>
              <input 
                placeholder="Search for products, brands and more" 
                className="w-full pl-11 pr-4 h-11 rounded-2xl bg-slate-100 border border-transparent focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-[14px] transition"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex items-center gap-1 ml-auto">
            <button 
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              className="md:hidden w-10 h-10 grid place-items-center rounded-xl hover:bg-slate-100"
            >
              <i className="ri-search-line text-xl"></i>
            </button>
            
            {/* User dropdown */}
            <div className="relative" ref={menuRef}>
              {!isLoggedIn ? (
                <button 
                  onClick={() => setLoginModalOpen(true)}
                  className="hidden sm:grid w-10 h-10 place-items-center rounded-xl hover:bg-slate-100"
                >
                  <i className="ri-user-3-line text-xl"></i>
                </button>
              ) : (
                <>
                  <button 
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="hidden sm:flex items-center gap-1 px-2 h-10 rounded-xl hover:bg-slate-100"
                  >
                    <i className="ri-user-3-fill text-xl text-indigo-600"></i>
                    <i className={`ri-arrow-down-s-line text-sm transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                      <div className="px-4 py-2 border-b border-slate-100">
                        <p className="text-sm font-medium text-slate-900">{userEmail}</p>
                        <p className="text-xs text-slate-500">Signed in</p>
                      </div>
                      <button 
                        onClick={() => handleNavigation('/profile')}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <i className="ri-user-settings-line"></i> Profile Details
                      </button>
                      <button 
                        onClick={() => handleNavigation('/order-history')}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <i className="ri-history-line"></i> Order History
                      </button>
                      <button 
                        onClick={() => handleNavigation('/track-order')}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <i className="ri-map-pin-line"></i> Track Your Order
                      </button>
                      <hr className="my-1" />
                      <button 
                        onClick={() => { logout(); setUserMenuOpen(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <i className="ri-logout-box-r-line"></i> Logout
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <button className="w-10 h-10 grid place-items-center rounded-xl hover:bg-slate-100 relative">
              <i className="ri-heart-3-line text-xl"></i>
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center bg-rose-600 text-white text-[10px] font-bold rounded-full">
                {wishlistCount}
              </span>
            </button>
            
            <button 
              onClick={() => setCartOpen(true)}
              className="w-10 h-10 grid place-items-center rounded-xl hover:bg-slate-100 relative"
            >
              <i className="ri-shopping-bag-3-line text-xl"></i>
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center bg-indigo-600 text-white text-[10px] font-bold rounded-full">
                {cartCount}
              </span>
            </button>
          </div>
        </div>
      </div>

      {mobileSearchOpen && (
        <div className="md:hidden border-t border-slate-200 px-4 py-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input 
              placeholder="Search for products..." 
              className="w-full pl-10 pr-4 h-11 rounded-2xl bg-slate-100 outline-none"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
        </div>
      )}
    </header>
  );
}